import { http } from '@utils/http';
import { isDev } from '@utils/env';
import { createLogger } from '@utils/logger';
import {
  isParentBridgeAvailable,
  MINDCLOUD_BRIDGE_EVENTS,
  notifyExplorerReady,
  requestParentAuth,
  requestParentLogin,
  requestParentLogout,
  subscribeParentMessages,
  type MindcloudAuthPayload,
} from './parentBridge';

const log = createLogger('auth');

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuthUser {
  id: string;
  name: string;
  avatar?: string;
  email?: string;
  phone?: string;
}

/** /api/v1/user/info 返回的原始用户数据 */
interface CloudUserInfoData {
  id: number;
  name: string;
  phone: string;
  email: string;
  photo: string;
}

export interface AuthAdapter {
  /** 恢复持久化登录态 */
  init(): Promise<void>;
  /** 触发登录流程 */
  login(): Promise<void>;
  /** 登出并清理持久化数据 */
  logout(): Promise<void>;
  /** 当前 token，null = 未登录 */
  getToken(): string | null;
  /** 当前用户信息 */
  getUser(): AuthUser | null;
  /** iframe 内需等父页面明确返回 auth-state；独立模式默认已就绪。 */
  isAuthSettled?(): boolean;
  /** 订阅登录态变更，返回取消订阅函数 */
  onAuthChange(callback: (user: AuthUser | null) => void): () => void;
}

// ---------------------------------------------------------------------------
// Storage keys
// ---------------------------------------------------------------------------

const STORAGE_KEY_TOKEN = 'token';
const STORAGE_KEY_USER = 'user';
const PARENT_AUTH_SETTLE_TIMEOUT_MS = 5_000;

/** 将云端原始用户数据映射为应用内 AuthUser */
function mapCloudUser(d: CloudUserInfoData): AuthUser {
  return {
    id: String(d.id),
    name: d.name,
    avatar: d.photo || undefined,
    email: d.email || undefined,
    phone: d.phone || undefined,
  };
}

function readString(source: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value) return value;
    if (typeof value === 'number') return String(value);
  }
  return undefined;
}

function mapParentUser(raw: unknown): AuthUser | null {
  if (!raw || typeof raw !== 'object') return null;
  const source = raw as Record<string, unknown>;
  const id = readString(source, ['id', 'user_id', 'userId', 'uid']);
  const name = readString(source, ['name', 'nickname', 'user_name', 'username', 'email', 'mobile']);
  if (!id && !name) return null;
  return {
    id: id ?? name ?? 'parent-user',
    name: name ?? id ?? '已登录用户',
    avatar: readString(source, ['avatar', 'photo', 'icon_url', 'iconUrl']),
    email: readString(source, ['email']),
    phone: readString(source, ['phone', 'mobile']),
  };
}

// ---------------------------------------------------------------------------
// DevTokenAdapter — 开发环境 / URL 含 ?token= 时激活
// ---------------------------------------------------------------------------

class DevTokenAdapter implements AuthAdapter {
  private token: string | null = null;
  private user: AuthUser | null = null;
  private listeners = new Set<(user: AuthUser | null) => void>();

  async init(): Promise<void> {
    // 1. 从 URL 提取 token（优先）
    const url = new URL(window.location.href);
    const urlToken = url.searchParams.get('token');

    if (urlToken) {
      this.token = urlToken;
      localStorage.setItem(STORAGE_KEY_TOKEN, urlToken);
      // 从 URL 移除 token，避免泄露到浏览器历史
      url.searchParams.delete('token');
      window.history.replaceState(null, '', url.toString());
      log.info('Token extracted from URL and persisted');
    } else {
      // 2. 从 localStorage 恢复
      this.token = localStorage.getItem(STORAGE_KEY_TOKEN);
    }

    // 通过云端接口获取真实用户信息
    if (this.token) {
      try {
        const rawUser = await this.fetchRawUserInfo(this.token);
        // localStorage 存完整 response.data，与主站一致
        localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(rawUser));
        this.user = mapCloudUser(rawUser);
      } catch (e) {
        log.warn('Failed to fetch user info, falling back to cache', e);
        // 降级到本地缓存
        const cached = localStorage.getItem(STORAGE_KEY_USER);
        if (cached) {
          try {
            this.user = mapCloudUser(JSON.parse(cached) as CloudUserInfoData);
          } catch {
            localStorage.removeItem(STORAGE_KEY_USER);
          }
        }
        // token 无效时清理登录态
        if (!this.user) {
          this.token = null;
          localStorage.removeItem(STORAGE_KEY_TOKEN);
        }
      }
    }

    this.notify();
  }

  /** 调用 /api/v1/user/info，返回原始 data 对象 */
  private async fetchRawUserInfo(token: string): Promise<CloudUserInfoData> {
    const resp = await http.post<CloudUserInfoData>('/api/v1/user/info', undefined, {
      headers: { token },
    });
    return resp.data;
  }

  async login(): Promise<void> {
    // DevToken 模式下无交互式登录，提示开发者通过 URL 传入 token
    log.warn('DevTokenAdapter: use ?token=<jwt> to authenticate');
  }

  async logout(): Promise<void> {
    this.token = null;
    this.user = null;
    localStorage.removeItem(STORAGE_KEY_TOKEN);
    localStorage.removeItem(STORAGE_KEY_USER);
    this.notify();
    log.info('Logged out');
  }

  getToken(): string | null {
    return this.token;
  }

  getUser(): AuthUser | null {
    return this.user;
  }

  isAuthSettled(): boolean {
    return true;
  }

  onAuthChange(callback: (user: AuthUser | null) => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  private notify(): void {
    this.listeners.forEach((cb) => cb(this.user));
  }
}

class ParentWindowAuthAdapter implements AuthAdapter {
  private token: string | null = null;
  private user: AuthUser | null = null;
  private listeners = new Set<(user: AuthUser | null) => void>();
  private unsubscribe: (() => void) | null = null;
  private receivedAuth = false;
  private readonly handleOnline = () => {
    if (!this.token) requestParentAuth();
  };

  async init(): Promise<void> {
    this.ensureListening();
    notifyExplorerReady();
    requestParentAuth();

    if (this.receivedAuth) return;
    await new Promise<void>((resolve) => {
      let unsubscribe: () => void = () => undefined;
      const timer = setTimeout(() => {
        this.applyAuthPayload(undefined);
      }, PARENT_AUTH_SETTLE_TIMEOUT_MS);
      const finish = () => {
        clearTimeout(timer);
        unsubscribe();
        resolve();
      };
      unsubscribe = this.onAuthChange(finish);
      if (!navigator.onLine) {
        this.applyAuthPayload(undefined);
      }
    });
  }

  async login(): Promise<void> {
    requestParentLogin();
  }

  async logout(): Promise<void> {
    requestParentLogout();
    this.token = null;
    this.user = null;
    this.notify();
  }

  getToken(): string | null {
    return this.token;
  }

  getUser(): AuthUser | null {
    return this.user;
  }

  isAuthSettled(): boolean {
    return this.receivedAuth;
  }

  onAuthChange(callback: (user: AuthUser | null) => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  private ensureListening(): void {
    if (this.unsubscribe) return;
    addEventListener('online', this.handleOnline);
    this.unsubscribe = subscribeParentMessages((message) => {
      if (
        message.type !== MINDCLOUD_BRIDGE_EVENTS.hostAuthState &&
        message.type !== MINDCLOUD_BRIDGE_EVENTS.hostLoginResult
      )
        return;
      this.applyAuthPayload(message.payload as MindcloudAuthPayload | undefined);
    });
  }

  private applyAuthPayload(payload?: MindcloudAuthPayload): void {
    const token = payload?.token || null;
    const user = mapParentUser(payload?.user);
    this.token = token;
    this.user =
      token && (payload?.isAuthenticated || user)
        ? (user ?? { id: 'parent-user', name: '已登录用户' })
        : null;
    this.receivedAuth = true;
    this.notify();
  }

  private notify(): void {
    this.listeners.forEach((cb) => cb(this.user));
  }
}

// ---------------------------------------------------------------------------
// Adapter factory
// ---------------------------------------------------------------------------

function createAuthAdapter(): AuthAdapter {
  if (isParentBridgeAvailable()) {
    return new ParentWindowAuthAdapter();
  }

  // 开发环境或 URL 含 token 参数时使用 DevTokenAdapter
  if (isDev || new URLSearchParams(window.location.search).has('token')) {
    return new DevTokenAdapter();
  }

  // TODO(auth-phase4): MicroAppAdapter / IframeAdapter / StandaloneAdapter
  // 当前阶段其他环境也降级到 DevTokenAdapter
  return new DevTokenAdapter();
}

export const authAdapter = createAuthAdapter();
